/**
 * WordPress dependencies
 */
import { useSelect } from '@safe-wordpress/data';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { HelpIcon } from '@nab/components';
import { store as NAB_EXPERIMENTS } from '@nab/experiments';
import { formatNumber } from '@nab/i18n';
import type { ExperimentType, ExperimentTypeName, Maybe } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { useExperimentAttribute } from '../hooks';

type SummaryType =
	| 'loading'
	| 'winner'
	| 'possible-winner'
	| 'no-winner'
	| 'collecting-results';

export type TypicalSampleSizeProps = {
	readonly type: SummaryType;
	readonly confidence: Maybe< number >;
	readonly minConfidence: Maybe< number >;
};

export const TypicalSampleSize = ( {
	type,
	confidence = 50,
	minConfidence = 90,
}: TypicalSampleSizeProps ): JSX.Element | null => {
	const typeName = useExperimentAttribute( 'type' );
	const status = useExperimentAttribute( 'status' );
	const { expectedPageViews, singleExpectedPageViews } =
		useExpectedPageViews( typeName );

	if ( ! expectedPageViews ) {
		return null;
	}

	if (
		'loading' === type ||
		'winner' === type ||
		( 'possible-winner' === type &&
			confidence >= Math.min( 90, minConfidence ) )
	) {
		// TODO DAVID. Restore.
		return <p>SOMETHING</p>;
		// return null;
	}

	if ( 'finished' === status ) {
		// TODO DAVID. Restore.
		return <p>FINISHED</p>;
		// return null;
	}

	return (
		<div className="nab-summary__typical-sample-size">
			<span>
				{ sprintf(
					'%1$s: %2$s – %3$s',
					_x( 'Typical Sample Size', 'text', 'nelio-ab-testing' ),
					formatNumber( expectedPageViews.p25 ),
					formatNumber( expectedPageViews.p75 )
				) }
			</span>{ ' ' }
			<HelpIcon
				text={
					sprintf(
						/* translators: %1$s: Number of page views. %2$s: Number of page views. %3$d: Percentage value. %4$s: Number of page views.    */
						_x(
							'Similar tests typically require %1$s–%2$s tested page views to reach %3$d%% confidence. Half of tests finish after about %4$s page views, although yours may require more depending on traffic and conversion rates.',
							'text',
							'nelio-ab-testing'
						),
						formatNumber( expectedPageViews.p25 ),
						formatNumber( expectedPageViews.p75 ),
						90,
						formatNumber( expectedPageViews.median )
					) +
					( singleExpectedPageViews
						? ' ' +
						  sprintf(
								/* translators: %1$s: Number of page views. %2$s: Number of page views.*/
								_x(
									'If the scope is limited to a single page, the test may only require %1$s–%2$s tested page views instead.',
									'text',
									'nelio-ab-testing'
								),
								formatNumber( singleExpectedPageViews.p25 ),
								formatNumber( singleExpectedPageViews.p75 )
						  )
						: '' )
				}
			/>
		</div>
	);
};

// =====
// HOOKS
// =====

const useExpectedPageViews = ( typeName?: ExperimentTypeName ) => {
	const scope = useExperimentAttribute( 'scope' );
	return useSelect(
		( select ) => {
			const { getExperimentTypes } = select( NAB_EXPERIMENTS );

			const experimentTypes = getExperimentTypes();
			const expectedPageViews =
				experimentTypes[ typeName ?? '' ]?.expected.pageViews;
			const singleExpectedPageViews = maybeGetSingleExpectedPageViews(
				typeName ?? '',
				experimentTypes
			);

			const runsOnSingleUrl =
				scope?.length === 1 &&
				scope?.[ 0 ]?.attributes.type === 'exact';

			return {
				expectedPageViews: runsOnSingleUrl
					? singleExpectedPageViews ?? expectedPageViews
					: expectedPageViews,
				singleExpectedPageViews: ! runsOnSingleUrl
					? singleExpectedPageViews
					: undefined,
			};
		},
		[ scope, typeName ]
	);
};

// =======
// HELPERS
// =======

function maybeGetSingleExpectedPageViews(
	typeName: ExperimentTypeName,
	experimentTypes: Record< ExperimentTypeName, ExperimentType >
) {
	if ( ! experimentTypes[ typeName ]?.expected.pageStatsOnSingleScope ) {
		return undefined;
	}

	const expectedPageViews = experimentTypes[ typeName ]?.expected.pageViews;
	if ( ! expectedPageViews ) {
		return undefined;
	}

	const singleExpectedPageViews =
		experimentTypes[ 'nab/page' ]?.expected.pageViews;
	if ( ! singleExpectedPageViews ) {
		return undefined;
	}

	if (
		areExpectationsTooSimilar( expectedPageViews, singleExpectedPageViews )
	) {
		return undefined;
	}

	return singleExpectedPageViews;
}

function areExpectationsTooSimilar(
	expectations: ExperimentType[ 'expected' ][ 'pageViews' ],
	singleExpectations: ExperimentType[ 'expected' ][ 'pageViews' ]
) {
	return (
		singleExpectations.p25 / expectations.p25 > 0.7 &&
		singleExpectations.p75 / expectations.p75 > 0.7
	);
}
